'use client';

import { useState, useEffect, ReactNode } from 'react';

interface HydrationBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Universal hydration boundary component that prevents hydration mismatches
 * by ensuring components only render after client-side hydration is complete.
 * 
 * Use this to wrap components that:
 * - Use browser APIs (window, localStorage, etc.)
 * - Have dynamic content that differs between server and client
 * - Cause hydration mismatch errors
 */
export function HydrationBoundary({ children, fallback = null }: HydrationBoundaryProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Higher-order component version for wrapping components
 */
export function withHydrationBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function HydratedComponent(props: P) {
    return (
      <HydrationBoundary fallback={fallback}>
        <Component {...props} />
      </HydrationBoundary>
    );
  };
}
